import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import StyledExpandableText from '../styled/StyledExpandableText';
import { useImageCalculatedHeight } from '@/hooks/useImageCalculatedHeight';
import { Control, useWatch } from 'react-hook-form';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import StyledInput from '../styled/StyledInput';
import StyledTextarea from '../styled/StyledTextarea';
import StyledButton from '../styled/StyledButton';
import { useTranslation } from '@/i18n';

interface CircuitItineraryCardProps {
  itinerary: {
    stepTitle?: string;
    description?: string;
    images?: string[];
  }[];
  control?: Control<EstimateFormData>;
  onValidateParagraph?: (field: string, value: string) => Promise<void>;
}

type ItineraryItem = {
  day: CircuitItineraryCardProps['itinerary'][number];
  position: 'left' | 'right';
  nextPosition: 'left' | 'right';
};

const CircuitDetailsItineraryCard = ({
  itinerary,
  control,
  onValidateParagraph,
}: CircuitItineraryCardProps) => {
  const { t } = useTranslation();
  const [isFolded, setIsFolded] = useState(false);

  const itineraryWithAlternatedImages = useMemo(
    () =>
      itinerary.reduce(
        (acc, day, index) => [
          ...acc,
          {
            day,
            position: index === 0 ? 'left' : acc[acc.length - 1].nextPosition,
            nextPosition:
              day.images && day.images.length > 0
                ? index === 0
                  ? 'right'
                  : acc[acc.length - 1].nextPosition === 'right'
                    ? 'left'
                    : 'right'
                : index === 0
                  ? 'left'
                  : acc[acc.length - 1].nextPosition,
          } as ItineraryItem,
        ],
        [] as ItineraryItem[],
      ),
    [itinerary],
  );

  return (
    <Card className="p-4">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle>
          {t('circuits.editorial.details.detailedItinerary')}
        </CardTitle>
        {isFolded ? (
          <ChevronDown
            className="size-5 hover:cursor-pointer"
            onClick={() => setIsFolded(false)}
          />
        ) : (
          <ChevronUp
            className="size-5 hover:cursor-pointer"
            onClick={() => setIsFolded(true)}
          />
        )}
      </CardHeader>
      {!isFolded && (
        <CardContent className="flex flex-col w-full gap-6">
          {itineraryWithAlternatedImages.map(
            ({ day: { stepTitle, description, images }, position }, index) =>
              control ? (
                <ControlledSingleDayRecap
                  key={`itinerary-${index}`}
                  control={control}
                  stepTitle={stepTitle}
                  description={description}
                  image={images && images.length > 0 ? images[0] : undefined}
                  index={index}
                  imagePosition={position}
                  onValidateParagraph={onValidateParagraph}
                />
              ) : (
                <SingleDayRecap
                  key={`itinerary-${index}`}
                  stepTitle={stepTitle}
                  description={description}
                  image={images && images.length > 0 ? images[0] : undefined}
                  index={index}
                  imagePosition={position}
                />
              ),
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default CircuitDetailsItineraryCard;

interface SingleDayRecapProps {
  stepTitle?: string;
  description?: string;
  image?: string;
  index: number;
  imagePosition: 'left' | 'right';
  control?: Control<EstimateFormData>;
  onValidateParagraph?: (field: string, value: string) => Promise<void>;
}

interface ControlledSingleDayRecapProps extends SingleDayRecapProps {
  control: Control<EstimateFormData>;
}

const ControlledSingleDayRecap = ({
  control,
  index,
  ...props
}: ControlledSingleDayRecapProps) => {
  const stepTitle = useWatch({
    control,
    name: `circuit.circuitDays.${index}.stepTitle`,
  });
  const description = useWatch({
    control,
    name: `circuit.circuitDays.${index}.description`,
  });

  return (
    <SingleDayRecap
      {...props}
      index={index}
      stepTitle={stepTitle}
      description={description}
      control={control}
    />
  );
};

const SingleDayRecap = ({
  stepTitle,
  description,
  image,
  index,
  imagePosition,
  control,
  onValidateParagraph,
}: SingleDayRecapProps) => {
  const { t } = useTranslation();
  const { imgRef, calculatedHeight } = useImageCalculatedHeight({
    defaultHeight: 400,
  });

  const [isEditEnabled, setIsEditEnabled] = useState(false);

  const handleValider = async () => {
    if (onValidateParagraph) {
      if (stepTitle != null) {
        await onValidateParagraph(`dayTitle_${index}`, stepTitle);
      }
      if (description != null) {
        await onValidateParagraph(`dayDescription_${index}`, description);
      }
    }
    setIsEditEnabled(false);
  };

  return (
    <div className="flex flex-row gap-1 items-start rounded-lg">
      {isEditEnabled && control !== undefined ? (
        <div className="flex flex-col w-full gap-2">
          <StyledInput
            control={control}
            name={`circuit.circuitDays.${index}.stepTitle`}
            title={t('circuits.editorial.details.title')}
          />
          <StyledTextarea
            control={control}
            name={`circuit.circuitDays.${index}.description`}
            title={t('circuits.editorial.details.description')}
            rows={3}
          />
          <StyledButton title={t('common.validate')} onClick={handleValider} />
        </div>
      ) : (
        <div className="w-full min-h-40">
          {image && (
            <div
              className={clsx('w-1/2 h-fit', {
                'float-left mr-4': imagePosition === 'left',
                'float-right ml-4': imagePosition === 'right',
              })}
            >
              <img
                className="w-full h-auto object-cover object-center min-h-72 max-h-96 rounded-lg"
                src={image ?? '/images/no_itinerary_image.png'}
                alt="image"
                ref={imgRef}
              />
            </div>
          )}
          <h2 className="text-lg font-medium text-black">
            {t('circuits.editorial.details.day')} {index + 1} : {stepTitle}
          </h2>
          {description && (
            <StyledExpandableText
              text={description}
              maxHeight={calculatedHeight}
            />
          )}
        </div>
      )}
      {control !== undefined && !isEditEnabled && (
        <StyledTooltipContainer
          tooltipMessage={t('circuits.editorial.details.edit')}
        >
          <EditIcon
            className="size-4 hover:cursor-pointer stroke-brand-700"
            onClick={() => setIsEditEnabled(true)}
          />
        </StyledTooltipContainer>
      )}
    </div>
  );
};
